import { useEffect, useRef, useState, useMemo } from "react"
import { useLocation } from "react-router-dom"
import { useSignageStore, useAuthStore } from "@stores"
import { buildUrl, isSignagePage } from "@utils/helpers"
import { isDevelopmentEnvironment, isLocalEnvironment } from "@constants"
import { initializeDeviceSocket, getSocket, getChannelName } from "@/connections/socket"

export type SocketEnabled<Raw> = boolean | ((event: string, data: Raw) => boolean)

type SocketOptions<Raw> = {
  eventName?: string | string[]
  isPrivate?: boolean
  enabled?: SocketEnabled<Raw>
  handler?: (event: string, message: Raw) => void
}
async function resolveEchoAndChannel(preferredChannel?: string) {
  let echoInstance = getSocket()
  let resolvedChannel = preferredChannel ?? getChannelName()

  if (!echoInstance || !resolvedChannel) {
    const result = await initializeDeviceSocket(resolvedChannel as string)

    echoInstance = result.echo
    resolvedChannel = preferredChannel ?? result.channelName
  }

  return { echoInstance, resolvedChannel }
}

export const logger = (prefix: string, logInfo: object = {}) => {
  if (isDevelopmentEnvironment() || isLocalEnvironment()) {
    const message = Object.entries(logInfo)
      .map(([name, content]) => {
        return `${name}: ${JSON.stringify(content)}`
      })
      .join(",")

    // eslint-disable-next-line no-console
    console.log(`[${prefix}]`, message)
  }
}

function createDispatcher<Raw>(
  enabledRef: React.MutableRefObject<SocketEnabled<Raw>>,
  handlerRef: React.MutableRefObject<((event: string, message: Raw) => void) | undefined>
) {
  return function dispatch(event: string, data: Raw) {
    const currentEnabled = enabledRef.current

    logger("useSocket - Dispatcher", { event, data })

    if (typeof currentEnabled === "function" && !currentEnabled(event, data)) return

    handlerRef.current?.(event, data)
  }
}

const LISTEN_ALL_KEY = "__listenToAll__"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function bindChannelListeners<Raw>(
  channel: any,
  eventName: string | string[] | undefined,
  dispatch: (event: string, data: Raw) => void
): Record<string, (data: Raw) => void> {
  let eventNames = Array.isArray(eventName) ? eventName : [eventName ?? ""]

  eventNames = eventNames.filter((name) => name)
  const activeListeners: Record<string, (data: Raw) => void> = {}

  if (eventNames.length > 0) {
    eventNames.forEach((name) => {
      const listener = (data: Raw) => dispatch(name, data)

      channel.listen(name, listener)
      activeListeners[name] = listener
    })
    logger("useSocket - Binding Event", { event: eventNames })
  } else {
    const listener = (event: string, data: Raw) => dispatch(event, data)

    channel.listenToAll(listener)
    activeListeners[LISTEN_ALL_KEY] = listener as unknown as (data: Raw) => void
    logger("useSocket - Binding to all events")
  }

  return activeListeners
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function unbindChannelListeners<Raw>(channel: any, activeListeners: Record<string, (data: Raw) => void>) {
  if (!channel) return

  Object.entries(activeListeners).forEach(([name, listener]) => {
    if (name === LISTEN_ALL_KEY) {
      channel.stopListeningToAll(listener)
    } else {
      channel.stopListening(name, listener)
    }
  })
}

export function useSocket<Raw = unknown>(options: SocketOptions<Raw>) {
  const { isPrivate = true, eventName, handler, enabled = true } = options
  const [connected, setConnected] = useState(false)
  const [channelName, setChannelName] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const handlerRef = useRef(handler)
  const enabledRef = useRef(enabled)
  const pathname = useLocation().pathname
  const signageClientId = useSignageStore((state) => state.clientId)
  const adminClientId = useAuthStore((state) => state.user.client_id)
  const clientId = useMemo(() => {
    if (isSignagePage()) {
      return signageClientId
    }

    return adminClientId
  }, [pathname, signageClientId, adminClientId])

  useEffect(() => {
    if (!!clientId) {
      const channelFormat = process.env.APP_SOCKET_CHANNEL || "client.{clientId}"

      setChannelName(buildUrl(channelFormat, { clientId }))
    }
  }, [clientId])

  useEffect(() => {
    handlerRef.current = handler
  }, [handler])

  useEffect(() => {
    enabledRef.current = enabled
  }, [enabled])

  // When `enabled` is a plain boolean false, skip connecting entirely.
  const shouldConnect = typeof enabled === "boolean" ? enabled : true

  useEffect(() => {
    if (!shouldConnect || !channelName) return

    let cancelled = false
    let channel: any = undefined
    let currentListeners: Record<string, (data: Raw) => void> = {}

    function subscribeAndListen(echoInstance: ReturnType<typeof getSocket>, cleanName: string) {
      if (cancelled || !echoInstance) return

      // eslint-disable-next-line no-console
      console.log(`[useSocket] Connected — listening on ${isPrivate ? "private-" : ""}${cleanName}`)

      channel = isPrivate ? echoInstance.private(cleanName) : echoInstance.channel(cleanName)
      const dispatch = createDispatcher<Raw>(enabledRef, handlerRef)

      currentListeners = bindChannelListeners<Raw>(channel, eventName, dispatch)
      setConnected(true)
    }

    async function connect() {
      try {
        setError(null)
        setConnected(false)

        const { echoInstance, resolvedChannel } = await resolveEchoAndChannel(channelName as string)

        if (cancelled) return

        setChannelName(resolvedChannel)

        const cleanName = resolvedChannel.replace(/^private-/, "")

        subscribeAndListen(echoInstance, cleanName)
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : "Socket connection failed."

          // eslint-disable-next-line no-console
          console.error("[useSocket] Connection error:", message)
          setError(message)
        }
      }
    }

    connect()

    return () => {
      logger("useSocket - Unbind Channel Listener")
      unbindChannelListeners(channel, currentListeners)
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldConnect, channelName])

  return { connected, channelName, error }
}

export default useSocket
